import { CSSProperties, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { setAnchorPosition } from './set-anchor-position';
import { setTailPosition } from './set-tail-position';
import { usePositionOnScroll } from './use-position-on-scroll';
import { TooltipPortalContentDirection, TooltipPortalPosition } from './tooltip-portal-types';
import Portal from 'utils/react-portal';
import { OptionalComponent } from 'utils/optional-component';

import './tooltip-portal-v2.css';

/* 
	TIPS:
		- if you want to apply hover styles on the parent component your tooltip lives in
			you can use css `&:has(.tooltip-portal-wrapper.active) { ... }` to apply styles.
			There is a good example of that in `theme-pack-overview-card-slim.scss`
*/

type ITooltipPortalV2 = PropsWithChildren<{
	/** the actual content in the tooltip */
	tooltipContent: JSX.Element;
	position?: TooltipPortalPosition;
	contentDirection?: TooltipPortalContentDirection;
	styles?: {
		/** backgroundColor will override mode background-color */
		backgroundColor?: CSSProperties['backgroundColor'];
		/** color will override mode color */
		color?: CSSProperties['color'];
		width?: CSSProperties['width'];
		padding?: CSSProperties['padding'];
		borderRadius?: CSSProperties['borderRadius'];
		boxShadow?: CSSProperties['boxShadow'];
		fontSize?: CSSProperties['fontSize'];
	};
	portalWrapperStyles?: React.CSSProperties;
	/** custom style background and color will override this light/dark mode */
	mode?: 'light' | 'dark';
	/**
	 * A positive number to adjust the left/right tooltip tooltop position when using top/bottom position with ltr/rtl contentDirection.
	 *
	 * Useful when increasing the border-radius of the tooltip.
	 *
	 * Note: this is not used to "guess" the position. It shifts the tooltip content by the number of pixels provided while maintaining the correct tail position.
	 * */
	horizontalOffset?: number;
	verticalOffset?: number;
	tooltipWrapperClass?: string;
	childrenWrapperClass?: string;
	contentWrapperClass?: string;
	portalHover?: (isHovered: boolean) => void;
	stopPropagation?: boolean;
	disabled?: boolean;
	delayStart?: number;
	/** tailSize is a number that is converted to px */
	tailSize?: number;
	keepOpenOnContentHover?: boolean;
	animationType?: 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right' | 'fade';
	slideDisplacement?: number | string;
	/** only use open if you want the tooltip to be controlled */
	open?: boolean;
	applyActiveSvgFillColor?: boolean;
}>;

const TooltipPortalV2 = ({
	children,
	tooltipContent,
	position = TooltipPortalPosition.TOP,
	contentDirection = TooltipPortalContentDirection.CENTER,
	styles = {},
	portalWrapperStyles = {},
	horizontalOffset = 1, // using 1px to account for border radius
	verticalOffset = 0,
	mode = 'dark',
	childrenWrapperClass = '',
	contentWrapperClass = '',
	tooltipWrapperClass = '',
	portalHover,
	stopPropagation = true,
	disabled,
	delayStart = 0,
	tailSize = 7,
	keepOpenOnContentHover = true,
	animationType = 'fade',
	open,
	slideDisplacement = '10px',
	applyActiveSvgFillColor = false,
}: ITooltipPortalV2) => {
	const isControlled = open !== undefined;

	const tooltipChildrenRef = useRef<HTMLDivElement>(null);
	const portalContentRef = useRef<HTMLDivElement>(null);
	const tooltipContainerRef = useRef<HTMLDivElement>(null);
	const tailRef = useRef<HTMLSpanElement>(null);

	const displayTimeout = useRef<NodeJS.Timeout | null>(null);
	const delayTimeout = useRef<NodeJS.Timeout | null>(null);

	const [display, setDisplay] = useState('none');
	const [show, setShow] = useState(false);
	const [blockPointerEvents, setBlockPointerEvents] = useState(false);
	const [tailPositionSet, setTailPositionSet] = useState(false);
	const [anchorPositionSet, setAnchorPositionSet] = useState(false);

	useEffect(() => {
		// block pointer events until we have a chance to set the position
		// otherwise the tooltip will disappear if the content is hovered while it is still being positioned
		const isShowing = show && display === 'block';
		const positionsSet = tailPositionSet && anchorPositionSet;
		if (!keepOpenOnContentHover && isShowing && positionsSet) {
			setBlockPointerEvents(true);
		}
	}, [anchorPositionSet, display, keepOpenOnContentHover, show, tailPositionSet]);

	const setPositions = useCallback(() => {
		if (!tooltipChildrenRef.current || !portalContentRef.current || !tailRef.current) return;
		setAnchorPosition({
			tooltipChildrenRef,
			portalContentRef,
			tooltipContainerRef,
			position,
			contentDirection,
			horizontalOffset,
			tailSize,
			onComplete: () => {
				setAnchorPositionSet(true);
			},
		});

		setTailPosition({
			tooltipChildrenRef,
			portalContentRef,
			tailRef,
			position,
			contentDirection,
			horizontalOffset,
			tailSize,
			onComplete: () => {
				setTailPositionSet(true);
			},
		});
	}, [position, contentDirection, horizontalOffset, tailSize]);

	usePositionOnScroll({ setPositions, show, tooltipContainerRef });

	const handleShow = useCallback(async () => {
		if (disabled) return;
		if (displayTimeout.current) {
			clearTimeout(displayTimeout.current);
		}
		if (delayTimeout.current) {
			clearTimeout(delayTimeout.current);
		}
		if (delayStart) {
			delayTimeout.current = setTimeout(() => {
				requestAnimationFrame(setPositions);
				setDisplay('block');
				setShow(true);
			}, delayStart);
			return;
		}
		requestAnimationFrame(setPositions);
		setDisplay('block');
		setShow(true);
	}, [delayStart, disabled, setPositions]);

	const handleHide = useCallback(() => {
		if (disabled) return;
		if (delayTimeout.current) {
			clearTimeout(delayTimeout.current);
		}
		if (displayTimeout.current) {
			clearTimeout(displayTimeout.current);
		}
		setShow(false);
		displayTimeout.current = setTimeout(() => {
			setDisplay('none');
		}, 200);
	}, [disabled]);

	useEffect(() => {
		if (open !== undefined && !disabled) {
			if (open) {
				handleShow();
			} else {
				handleHide();
			}
		}
	}, [disabled, handleHide, handleShow, open]);

	const handlePortalEnter = useCallback(() => {
		if (disabled || isControlled) return;

		if (!keepOpenOnContentHover && !blockPointerEvents) {
			return handleHide();
		}

		portalHover?.(true);
	}, [blockPointerEvents, disabled, handleHide, isControlled, keepOpenOnContentHover, portalHover]);

	const handlePortalLeave = useCallback(() => {
		if (disabled || isControlled) return;
		portalHover?.(false);
	}, [disabled, isControlled, portalHover]);

	// because this component finds fixed positions, when the inner content changes, the position jumps
	// for example, if something takes a second to load within the tooltip, it will re-render, change the size
	// of the tooltip and cause the tooltip position to be off
	// so we need to correct the position when the tooltip height/width changes
	// Note: this resize observer does not trigger on browser resize. So it doesn't overfire
	useEffect(() => {
		const portalEl = portalContentRef.current;
		if (!portalEl || !show || display === 'none') return;
		let frame: number | undefined;
		// track element position
		const resizeObserver = new ResizeObserver((entries) => {
			if (frame) cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => {
				entries.forEach(() => {
					setPositions();
				});
			});
		});
		if (portalEl) {
			resizeObserver.observe(portalEl);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, [portalContentRef, show, display, setPositions]);

	const displayHoverFill = useCallback(() => {
		const isTopOrBottom = position === TooltipPortalPosition.TOP || position === TooltipPortalPosition.BOTTOM;
		return keepOpenOnContentHover && show && display !== 'none' && isTopOrBottom;
	}, [display, keepOpenOnContentHover, position, show]);

	return (
		<div
			className={`tooltip-portal-v2-wrapper ${show ? 'active' : ''} ${tooltipWrapperClass ?? ''}`}
			ref={tooltipContainerRef}
			onPointerEnter={isControlled ? undefined : handleShow}
			onPointerLeave={isControlled ? undefined : handleHide}
			role="tooltip"
			style={portalWrapperStyles}
		>
			<div
				ref={tooltipChildrenRef}
				className={`tooltip-portal-children ${childrenWrapperClass} ${show ? 'active' : ''} ${applyActiveSvgFillColor ? 'fill-active-color-svg' : ''}`}
			>
				{/* covers small gap between trigger element and tooltip which prevents hover */}
				<OptionalComponent display={displayHoverFill()}>
					<span
						className="tooltip-portal-hover-fill"
						style={{
							...(position === TooltipPortalPosition.TOP ? { bottom: '1em' } : {}),
							...(position === TooltipPortalPosition.BOTTOM ? { top: '1em' } : {}),
							height: `calc(100% + ${`${tailSize + 1 + Math.abs(verticalOffset ?? 0)}px`})`,
						}}
					/>
				</OptionalComponent>
				{children}
			</div>
			{/* Portal allows us to overflow any content on the page. It is set to position fixed using css */}
			<Portal className="tooltip-portal-v2">
				<div
					onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
					className={classNames('tooltip-portal-content', contentWrapperClass, animationType, {
						'light-mode': mode === 'light',
						active: show,
					})}
					style={
						{
							'--tooltip-portal-v2-width': styles.width,
							'--tooltip-portal-v2-background-color': styles.backgroundColor,
							'--tooltip-portal-v2-padding': styles.padding,
							'--tooltip-portal-v2-border-radius': styles.borderRadius,
							'--tooltip-portal-v2-color': styles.color,
							'--tooltip-portal-v2-box-shadow': styles.boxShadow,
							'--tooltip-portal-v2-font-size': styles.fontSize,
							'--tooltip-portal-v2-tail-size': `${tailSize}px`,
							'--tooltip-portal-v2-display': display,
							'--tooltip-portal-v2-vertical-offset': verticalOffset ? verticalOffset + 'px' : 0,
							'--tooltip-portal-v2-slide-displacement':
								typeof slideDisplacement === 'number' ? `${slideDisplacement}px` : slideDisplacement,
							...(blockPointerEvents ? { pointerEvents: 'none' } : {}),
						} as CSSProperties
					}
					ref={portalContentRef}
					onPointerEnter={handlePortalEnter}
					onPointerLeave={handlePortalLeave}
				>
					{tooltipContent}
					<span ref={tailRef} className="tooltip-portal-tail" />
				</div>
			</Portal>
		</div>
	);
};

export default TooltipPortalV2;
